import React from "react";

// constants and icons
import { LuLoader, LuCircleCheck } from "react-icons/lu";
import { TiDelete } from "react-icons/ti";

// types
import { IErrorWarning } from "../../../store/types/misc";
import { twMerge } from "tailwind-merge";
import { FileText } from "lucide-react";

type IUpload = {
  title: string;
  name: string;
  onChange: (file: File | File[]) => void;
  onDelete?: () => void;
  required?: boolean;
  completed: boolean;
  status: IErrorWarning;
  loading?: boolean;
  label?: string;
  className?: string;
  imgPreview?: string;
  multiple?: boolean;
  icon?: React.ReactNode;
};

const Upload: React.FC<IUpload> = ({
  title,
  name,
  onChange,
  onDelete,
  required,
  completed,
  status,
  loading,
  label,
  className,
  imgPreview,
  multiple,
  icon,
}) => {
  const toSentenceCase = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const hasError = status === "error: this field is required";
  const hasFile = !!imgPreview || completed;

  return (
    <div
      onClick={() => document?.getElementById(name)?.click()}
      className={twMerge(
        "relative w-full border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 overflow-hidden",
        !imgPreview &&
          "p-8 flex flex-col items-center justify-center text-center",
        hasError
          ? "border-red-500 bg-red-50 hover:border-red-600"
          : "border-gray-300 hover:border-primary-400 hover:bg-primary-50/20",
        className,
      )}
    >
      {imgPreview ? (
        <>
          <div className="relative w-full h-[213px]">
            <img
              loading="lazy"
              src={imgPreview}
              alt={title}
              className="w-full h-full object-cover"
            />

            {loading && (
              <div className="absolute top-0 left-0 w-full h-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                <LuLoader size={32} className="animate-spin text-white" />
              </div>
            )}
          </div>
          {hasError && (
            <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs py-1 px-2 text-center">
              {status}
            </div>
          )}
        </>
      ) : (
        <>
          {completed ? (
            ""
          ) : (
            <>
              {" "}
              {icon ? (
                // If custom icon is provided, render it
                React.isValidElement(icon) ? (
                  React.cloneElement(icon as React.ReactElement, {
                    size: 48,
                    className: hasError ? "text-red-400" : "text-gray-400",
                  })
                ) : (
                  icon
                )
              ) : (
                // Default icon
                <FileText
                  size={48}
                  className={hasError ? "text-red-400" : "text-gray-400"}
                />
              )}
            </>
          )}

          <div className="mt-2">
            {completed ? (
              ""
            ) : (
              <label className="text-lg font-medium text-gray-700">
                {title}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}

            <div className="mt-2">
              {loading ? (
                <div className="inline-flex flex-col items-center">
                  <LuLoader size={32} className="animate-spin text-gray-500" />
                  <span className="text-sm text-gray-600 mt-2">
                    Uploading <span className="animate-pulse">...</span>
                  </span>
                </div>
              ) : completed ? (
                <div className="inline-flex flex-col items-center justify-center">
                  <LuCircleCheck size={52} className="text-primary-500" />
                  <span className="text-base font-medium text-primary-600 mt-2">
                    {toSentenceCase(title)} successful
                  </span>
                </div>
              ) : (
                <div className="inline-flex flex-col items-center">
                  <p className="text-sm text-gray-500 mt-1">
                    Click to browse and select a file to upload
                  </p>
                  <div className="text-xs text-gray-400 mt-2 space-x-2">
                    <span>{label} (MAX. 10MB)</span>
                    {multiple && (
                      <>
                        <span>•</span>
                        <span>Multiple files allowed</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* delete icon - only show when a file has been uploaded */}
      {hasFile && onDelete && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 bg-white rounded-full z-50 shadow-md"
        >
          <TiDelete
            onClick={onDelete}
            size={32}
            className="cursor-pointer text-red-500 hover:text-red-600 transition-colors z-50"
          />
        </div>
      )}

      <input
        type="file"
        name={name}
        id={name}
        multiple={multiple}
        className="outline-none"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const { files } = e.currentTarget;
          if (files?.length) {
            const selectedFiles = Array.from(files);
            onChange(multiple ? selectedFiles : selectedFiles[0]);
            e.target.value = "";
          }
        }}
        hidden
      />
    </div>
  );
};

export default Upload;
